import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import find from "lodash/find";

const ITEM_TEMPLATE =
  "<div class='flex justify-between w-full'>" +
  "<span class='font-semibold'>${text}</span>" +
  "<span class='text-gray-500 dark:text-gray-400'>${isoCode}</span>" +
  "</div>";

/**
 * useCountryData — single source of truth for country-related data.
 *
 * Provides:
 *  - `defaultCountry` — pre-computed USA record from the countries reducer.
 *  - `resolvedCountry` — resolves an explicit `countryValue` (or the current
 *     company's country) to a `{ countryId, countryName, countryIsoCode }` object,
 *     falling back to `defaultCountry`.
 *  - `dataSource` / `handleChange` / `itemTemplate` — Syncfusion DropDownList
 *     helpers consumed by the CountrySelect component.
 *
 * @param {object}   [options]
 * @param {string}   [options.countryValue] — explicit country parameterId to resolve
 * @param {Function} [options.onChange]     — Syncfusion change handler for CountrySelect
 */
export default function useCountryData({ countryValue, onChange } = {}) {
  const { currentCompany: { countryId } } = useSelector(state => state.currentUser);
  const { countries: records, defaultCountry } = useSelector(state => state.countries);

  /* ── resolved country (replaces useDefaultCountry) ─────────────── */
  const countryToUse = countryValue || countryId;

  const resolvedCountry = useMemo(() => {
    if (!countryToUse) return undefined;
    const country = find(records, { parameterId: countryToUse });
    if (!country) return defaultCountry;
    return {
      countryId: country.parameterId,
      countryName: country.name,
      countryIsoCode: country.code,
    };
  }, [records, countryToUse, defaultCountry]);

  /* ── Syncfusion helpers (replaces old useCountryData) ──────────── */
  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(c => ({ text: c.name, value: c.parameterId, isoCode: c.code }));
  }, [records]);

  const handleChange = useCallback(
    e => {
      if (!e.itemData) { onChange?.(undefined); return; }
      onChange?.({ value: e.itemData.value, label: e.itemData.text, isoCode: e.itemData.isoCode });
    },
    [onChange],
  );

  return {
    /** Pre-computed USA record from the countries reducer */
    defaultCountry,
    /** Resolved country for the given countryValue / company country */
    resolvedCountry: countryToUse ? resolvedCountry : defaultCountry,
    /** Syncfusion DropDownList data source */
    dataSource,
    /** Syncfusion change handler */
    handleChange,
    /** Default country parameterId for CountrySelect value prop */
    defaultValue: defaultCountry?.countryId,
    /** Syncfusion item template */
    itemTemplate: ITEM_TEMPLATE,
  };
}

